import PropTypes from 'prop-types';
import React from 'react';
import DropDownCell from 'components/LineItemTable/dropDownCell';
import DocumentLink from 'components/LineItemTable/documentLink';
import InputCell from 'components/LineItemTable/inputCell';
import ItemEvents from 'components/LineItemTable/itemEvents';
import { EditableTableRow } from 'components/EditableTable';
import purchaseOrderShape, { purchaseOrderOptionsType } from 'types/purchaseOrder';
import documentType from 'types/documentUpload';
import { formatDollars } from '../../../../../../../../utils/lineItemUtils';
import Editable from 'mixins/editable';
import { error as parseError } from '../../../../../../../../utils';
import getCanUseDocumentOptions from '../../../../../../../../utils/documents';
import MediaQuery from 'react-responsive';
import S from './laborTables.scss';
import { CUT_POINT } from 'config/constants';

const INVOICE_STATUSES = ['Not Billable', 'Not Invoiced', 'Ready for Accounting', 'Recorded', 'Invoiced', 'Paid'];
const WR_INVOICE_STATUSES = ['Not Billable', 'Not Invoiced', 'Ready for Accounting'];
const SIGNED_DOCUMENT_TYPES = ['Signed Documents'];
const BILLABLE_DOCUMENT_TYPES = ['PO', 'Work Order'];


class PurchaseOrderRow extends Editable(React.Component) {
  state = {
    ...this.props.lineItem,
    number: this.props.lineItem.number || '',
  };

  handleChange = attr => (val) => {
    const value = this.sanatize(attr, val);
    this.setState({ [attr]: value });

    if (
      val
      && attr === 'signed_document_id'
      && (this.state.invoice_status === 'Not Invoiced' || !this.state.invoice_status)
    ) {
      this.setState({ invoice_status: 'Ready for Accounting' });
    }
  }

  handleNumberChange = this.handleChange('number');
  handleTotalChange = this.handleChange('total');
  handleInvoiceChange = this.handleChange('invoice_number');
  handleKindChange = this.handleChange('kind');
  handleSignedDocumentChange = this.handleChange('signed_document_id');
  handleBillableDocumentChange = this.handleChange('billable_document_id');
  handleInvoiceStatusChange = this.handleChange('invoice_status');

  handleSave = () => {
    this.props.onSave(this.state);
  }

  handleDelete = () => {
    this.props.onDelete(this.state.id);
  }

  formatOptions = options => options.map(option => (
    { value: option, label: option }
  ));

  getPurchaseOrderLabel = id => (this.props.purchaseOrderOptions.find(po => po.value === id) || {});

  validateNumber = (number) => {
    const regex = /^(?!00)\d{9}$|^PR\d{8}$|^\d{8}[AIW]$|^PRJ\d+$/;
    const ime_regex = /^\d{8}[AIW]$/;
    const rf_regex = /^PRJ\d+$/;
    const isProjectNumber = this.props.projectNumber === number
      && !this.props.projectNumber.match(ime_regex)
      && !this.props.projectNumber.match(rf_regex);
    return (number === null || number === '' || (regex.test(number))) && !isProjectNumber;
  }

  invalidNumber = lineItem => !this.validateNumber(lineItem.number) && lineItem.kind !== 'Special Order' && lineItem.invoice_status !== 'Not Billable';

  getPhaseLineItemTotal = id =>
    this.props.phaseLineItems
      .filter(i => i.purchase_order_id === id)
      .reduce((a, b) => a + b.total, 0) || 0;

  renderTableDesktop = () => {
    const { readOnly, documents, role, originalExists, toggleLineItem, expandableRow } = this.props;
    const lineItem = this.state;
    const deleteError = lineItem.errors && lineItem.errors.base && parseError('base', lineItem.errors);
    const invoiceStatuses = role !== 'workroom' ? INVOICE_STATUSES : WR_INVOICE_STATUSES;
    const status = lineItem.invoice_status;
    const hideDelete = role !== 'admin' && (status === 'Recorded' || status === 'Invoiced' || status === 'Paid');
    const rowProps = {
      isNew: lineItem.id < 0,
      onSave: this.handleSave,
      onDelete: this.handleDelete,
      deleteError,
      readOnly,
    };
    const poTypes = ['Original', 'Additional', 'Exception', 'Refund Rebill', 'Special Order'];
    if (originalExists && lineItem.kind !== 'Original') poTypes.shift();
    const phaseLineItemTotal = this.getPhaseLineItemTotal(lineItem.id) || 0;
    const totalMismatch = status === 'Ready for Accounting' &&
      lineItem.total !== parseFloat((phaseLineItemTotal || 0).toFixed(2));
    const message = totalMismatch ? 'PO & Line-Item totals don\'t match' : '';
    return (
      <EditableTableRow
        key={lineItem.id}
        id={lineItem.id}
        hideDelete={hideDelete}
        totalMismatch={totalMismatch}
        role={role}
        invalidNumber={this.invalidNumber(lineItem)}
        message={message}
        deleteDialog
        {...rowProps}
        toggleLineItem={toggleLineItem}
        expandableRow={expandableRow}
      >
        <ItemEvents
          display={!this.context.isEditable}
          label=""
          itemType="purchaseOrders"
          lineItem={lineItem}
          dataKeys={['number', 'kind', 'signed_document_name', 'billable_document_name', 'invoice_status', 'total', 'invoice_number']}
        />
        <InputCell
          attr="number"
          label="PO #"
          lineItem={lineItem}
          onChange={this.handleNumberChange}
          cellValue={this.getPurchaseOrderLabel(lineItem.id).label}
        />
        <DropDownCell
          attr="kind"
          label="Type"
          lineItem={lineItem}
          options={this.formatOptions(poTypes)}
          onChange={this.handleKindChange}
        />
        <DropDownCell
          attr="signed_document"
          isIdAttr
          label="Signed Document"
          lineItem={lineItem}
          options={getCanUseDocumentOptions(documents, SIGNED_DOCUMENT_TYPES)}
          onChange={this.handleSignedDocumentChange}
          clearable
        >
          <DocumentLink documents={documents} id={lineItem.signed_document_id} />
        </DropDownCell>
        <DropDownCell
          attr="billable_document"
          isIdAttr
          label="Billable Document"
          lineItem={lineItem}
          options={getCanUseDocumentOptions(documents, BILLABLE_DOCUMENT_TYPES)}
          onChange={this.handleBillableDocumentChange}
        >
          <DocumentLink documents={documents} id={lineItem.billable_document_id} />
        </DropDownCell>
        <DropDownCell
          attr="invoice_status"
          label="Invoice Status"
          lineItem={lineItem}
          options={this.formatOptions(invoiceStatuses)}
          onChange={this.handleInvoiceStatusChange}
        />
        <InputCell type="number" attr="total" label="Total" lineItem={lineItem} onChange={this.handleTotalChange} cellValue={formatDollars(lineItem.total)} />
        <InputCell attr="invoice_number" label="QB Invoice" lineItem={lineItem} onChange={this.handleInvoiceChange} />
      </EditableTableRow>
    );
  }

  renderTableMobile = () => {
    const { readOnly, documents, role, originalExists, toggleLineItem } = this.props;
    const lineItem = this.state;
    const deleteError = lineItem.errors && lineItem.errors.base && parseError('base', lineItem.errors);
    const invoiceStatuses = role !== 'workroom' ? INVOICE_STATUSES : WR_INVOICE_STATUSES;
    const status = lineItem.invoice_status;
    const hideDelete = role !== 'admin' && (status === 'Recorded' || status === 'Invoiced' || status === 'Paid');
    const rowProps = {
      isNew: lineItem.id < 0,
      onSave: this.handleSave,
      onDelete: this.handleDelete,
      deleteError,
      readOnly,
    };
    const poTypes = ['Original', 'Additional', 'Exception', 'Refund Rebill', 'Special Order'];
    if (originalExists && lineItem.kind !== 'Original') poTypes.shift();
    const phaseLineItemTotal = this.getPhaseLineItemTotal(lineItem.id) || 0;
    const totalMismatch = status === 'Ready for Accounting' &&
      lineItem.total !== parseFloat((phaseLineItemTotal || 0).toFixed(2));
    const message = totalMismatch ? 'PO & Line-Item totals don\'t match' : '';
    return (
      <EditableTableRow
        key={lineItem.id}
        id={lineItem.id}
        invalidNumber={this.invalidNumber(lineItem)}
        hideDelete={hideDelete}
        totalMismatch={totalMismatch}
        role={role}
        message={message}
        deleteDialog
        toggleLineItem={toggleLineItem}
        {...rowProps}
      >
        <InputCell attr="number" label="PO #" lineItem={lineItem} onChange={this.handleNumberChange} cellValue={this.getPurchaseOrderLabel(lineItem.id).label} />
        <DropDownCell
          attr="kind"
          label="Type"
          lineItem={lineItem}
          options={this.formatOptions(poTypes)}
          onChange={this.handleKindChange}
        />
        <DropDownCell
          attr="signed_document"
          isIdAttr
          label="Signed Document"
          lineItem={lineItem}
          options={getCanUseDocumentOptions(documents, SIGNED_DOCUMENT_TYPES)}
          onChange={this.handleSignedDocumentChange}
          clearable
        >
          <DocumentLink documents={documents} id={lineItem.signed_document_id} />
        </DropDownCell>
        <DropDownCell
          attr="billable_document"
          isIdAttr
          label="Billable Document"
          lineItem={lineItem}
          options={getCanUseDocumentOptions(documents, BILLABLE_DOCUMENT_TYPES)}
          onChange={this.handleBillableDocumentChange}
        >
          <DocumentLink documents={documents} id={lineItem.billable_document_id} />
        </DropDownCell>
        <DropDownCell
          attr="invoice_status"
          label="Invoice Status"
          lineItem={lineItem}
          options={this.formatOptions(invoiceStatuses)}
          onChange={this.handleInvoiceStatusChange}
        />
        <InputCell type="number" attr="total" label="Total" lineItem={lineItem} onChange={this.handleTotalChange} cellValue={formatDollars(lineItem.total)} />
        <InputCell attr="invoice_number" label="QB Invoice" lineItem={lineItem} onChange={this.handleInvoiceChange} />
        <td>
          <div>
            <table className={S.inlineTableItem}>
              <tbody className={S.inlineTableItem}>
                <tr className={S.iconWrapper}>
                  <ItemEvents
                    display={!this.context.isEditable}
                    label=""
                    itemType="purchaseOrders"
                    lineItem={lineItem}
                    dataKeys={['number', 'kind', 'signed_document_name', 'billable_document_name', 'invoice_status', 'total', 'invoice_number']}
                  />
                </tr>
              </tbody>
            </table>
          </div>
        </td>
      </EditableTableRow>
    );
  }

  render() {
    return (
      <MediaQuery minWidth={CUT_POINT}>
        {(matches) => {
          if (matches) {
            return this.renderTableDesktop();
          }
          return this.renderTableMobile();
        }}
      </MediaQuery>
    );
  }
}

PurchaseOrderRow.contextTypes = {
  isEditable: PropTypes.bool,
};

PurchaseOrderRow.defaultProps = {
  readOnly: false,
  purchaseOrderOptions: [],
  phaseLineItems: [],
  projectNumber: '',
  role: '',
};

PurchaseOrderRow.propTypes = {
  projectNumber: PropTypes.string,
  role: PropTypes.string,
  lineItem: PropTypes.shape(purchaseOrderShape).isRequired,
  originalExists: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(documentType).isRequired,
  readOnly: PropTypes.bool,
  purchaseOrderOptions: purchaseOrderOptionsType,
  phaseLineItems: PropTypes.arrayOf(PropTypes.object),
};

export default PurchaseOrderRow;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseAccounting/components/purchaseOrderRow.jsx